import { CampaignLists, EmailSendStatus, OptionsApi } from '@core/swagger-client';
import { Button, Close, ErrorMessage, Input, Search, Select } from '@core/ui';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

const PageSize = 25;

const formatDate = (value?: string | null) =>
    value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : '-';

const statusFilters = [
    EmailSendStatus.Sent,
    EmailSendStatus.Failed,
    EmailSendStatus.Blacklisted,
    EmailSendStatus.AlreadyInterested,
    EmailSendStatus.AlreadySentInAnotherList,
    EmailSendStatus.Pending
];

interface Props {
    webinarId: string;
    campaignId: string;
    lists?: CampaignLists;
}

export const CampaignRecipientsTable = ({ webinarId, campaignId, lists }: Props) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<EmailSendStatus | undefined>(undefined);

    const { data, error } = useQuery(
        OptionsApi.marketingCampaigns.getRecipients({
            webinarId,
            id: campaignId,
            skip: page * PageSize,
            take: PageSize,
            search: search.trim() || undefined,
            status
        })
    );

    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / PageSize));

    const allLists = [...(lists?.webinar ?? []), ...(lists?.global ?? [])];
    const listById = new Map(allLists.map((l) => [l.id, l.description]));

    const onSearch = (value: string) => {
        setSearch(value);
        setPage(0);
    };
    const onStatus = (value: string) => {
        setStatus(value === '' ? undefined : (Number(value) as EmailSendStatus));
        setPage(0);
    };

    return (
        <div>
            {error && <ErrorMessage apiError={error} classNames="mb-4" />}
            <div className="flex max-md:flex-col gap-4 justify-between mb-4">
                <div className="flex-1 max-w-160">
                    <Input
                        data-testid="campaign-recipients-search"
                        label={t('Campaign.Recipients.Search')}
                        value={search}
                        onChange={(event) => onSearch(event.target.value)}
                    >
                        <Search classNames="size-6 flex-none self-center -order-1 mr-4 opacity-75 stroke-current" />
                        {search.length > 0 && (
                            <Button iconbtn aria-label={t('Search.Button.Clear.AriaLabel')} onClick={() => setSearch('')}>
                                <Close classNames="size-6 stroke-current" />
                            </Button>
                        )}
                    </Input>
                </div>
                <div className="flex-1 max-w-160">
                    <Select
                        testId="campaign-recipients-status"
                        label={t('Campaign.Recipients.StatusFilter')}
                        value={status?.toString() ?? ''}
                        onChange={(event) => onStatus(event.target.value)}
                    >
                        <option value="">{t('Campaign.Recipients.AllStatuses')}</option>
                        {statusFilters.map((value) => (
                            <option key={value} value={value}>
                                {t(`Campaign.RecipientStatus.${EmailSendStatus[value]}`)}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>

            <div className="table-responsive bg-white rounded-lg shadow-md">
                <table className="table table-striped mt-2" data-testid="campaign-recipients-table">
                    <thead>
                        <tr>
                            <th>{t('Campaign.Recipients.Email')}</th>
                            <th>{t('Campaign.Recipients.Name')}</th>
                            <th>{t('Campaign.Recipients.Company')}</th>
                            <th>{t('Campaign.Recipients.List')}</th>
                            <th>{t('Campaign.Recipients.SentAt')}</th>
                            <th>{t('Campaign.Recipients.Status')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.items ?? []).map((recipient) => (
                            <tr key={recipient.id}>
                                <td>{recipient.email}</td>
                                <td>{[recipient.firstName, recipient.lastName].filter(Boolean).join(' ')}</td>
                                <td>{recipient.companyName}</td>
                                <td>{listById.get(recipient.sourceListId) ?? recipient.sourceListId}</td>
                                <td>{formatDate(recipient.sentAt)}</td>
                                <td>{t(`Campaign.RecipientStatus.${EmailSendStatus[recipient.status]}`)}</td>
                            </tr>
                        ))}
                        {total === 0 && (
                            <tr>
                                <td colSpan={6}>{t('Campaign.Recipients.Empty')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {pageCount > 1 && (
                <div className="flex items-center gap-4 mt-4">
                    <Button
                        variant="secondary-outline"
                        data-testid="campaign-recipients-prev"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                        {t('Generic.Pagination.Previous')}
                    </Button>
                    <span className="text-sm">{t('Generic.Pagination.PageOf', { page: page + 1, total: pageCount })}</span>
                    <Button
                        variant="secondary-outline"
                        data-testid="campaign-recipients-next"
                        disabled={page + 1 >= pageCount}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        {t('Generic.Pagination.Next')}
                    </Button>
                </div>
            )}
        </div>
    );
};
